import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { userRoles } from '../../../../environments/environment';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import { AddRegionComponent } from '../../../shared/components/dialog/add-region/add-region.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CommonDialogComponent } from '../../../shared/components/dialog/commom/commom-dialog.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

interface EventType {
  id: any;
  eventHostRegionId: string;
  eventHostRegionName: string;
  eventTypeId: string;
  status: string;
  eventHostRegionInitial: string;
  receivedAttendingPoints: number;
}

interface APIResponse {
  status: boolean;
  message: string;
  data: EventType | [];
}

@Component({
  selector: 'app-region',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ButtonComponent,
    MatDialogModule,
    AddRegionComponent,
    FormsModule,
  ],
  templateUrl: './region.component.html',
  styleUrls: ['./region.component.scss'],
})
export class RegionComponent implements OnInit {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild('addEventTypeContent') addEventTypeContent!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;

  loading = false;
  submitLoading = false;
  data: any[] = [];
  editingEventType: EventType | null = null;
  columns: string[] = ['s.No', 'region name', 'region initial', 'status'];
  fullData: any[] = [];
  search: string = '';
  userRoles = {
    addNew: false,
    genarateReport: false,
    listItem: {
      edit: false,
      statusChange: false,
    },
  };

  constructor(
    private dialog: MatDialog,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.fetchRegions();

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles = userRoles[user as keyof typeof userRoles]?.region;
    }
  }

  navigateReports(): void {
    this.router.navigate(['/reports/report-dashboard'], {
      state: { activeTab: 'Diversity' },
    });
  }

  fetchRegions(): void {
    this.loading = true;

    this.httpService.get<APIResponse>('eventhostregions?status=2').subscribe({
      next: (response) => {
        const dataArray = Array.isArray(response.data)
          ? response.data
          : response.data
            ? [response.data]
            : [];
        const previousSearchTerm = this.search;

        this.fullData = dataArray.map((item: any, index: number) => ({
          id: item.eventHostRegionId,
          's.No': index + 1,
          'region name': item.eventHostRegionName || '-',
          'region initial': item.eventHostRegionInitial || '',
          status: item.status || 'Inactive',
          eventHostRegionId: item.eventHostRegionId,
          eventTypeId: item.eventTypeId || '',
          receivedAttendingPoints: item.receivedAttendingPoints || 0,
          isStatusClickable: true,
        }));

        if (previousSearchTerm && previousSearchTerm.trim() !== '') {
          this.search = previousSearchTerm;
          this.applyFilter();
        } else {
          this.data = [...this.fullData];
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load Regions:', err);
        this.showErrorToast(err?.message);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private getStatusBooleanValue(status: string): boolean {
    return status === 'Active';
  }

  updateSearch(searchValue: string): void {
    this.search = searchValue;
    this.applyFilter();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  applyFilter(): void {
    if (!this.search || this.search.trim() === '') {
      this.data = [...this.fullData];
      return;
    }
    // Match only the columns on screen: the rows also carry ids, GUIDs and internal flags,
    // and searching those makes rows with no visible match appear. Collapse whitespace on
    // both sides so text copied off the screen still matches what is stored.
    const normalize = (value: unknown): string =>
      (value ?? '')
        .toString()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

    const searchTerm = normalize(this.search);
    this.data = this.fullData.filter((item) =>
      this.columns.some((column) => normalize(item[column]).includes(searchTerm)),
    );

    this.resetPagination();
    this.cdr.detectChanges();
  }

  private resetPagination(): void {
    this.data = [...this.data];
    if (this.tableComponent) {
      this.tableComponent.currentPage = 1;
      this.tableComponent.cdr?.detectChanges();
    }
  }

  onEventTypeFormSubmit(formData: any): void {
    const statusValue = formData.status;
    if (this.submitLoading) return;

    const payload = {
      eventHostRegionName: formData.eventHostRegionName,
      eventHostRegionInitial: formData.eventHostRegionInitial,
      status: statusValue,
    };

    this.submitLoading = true;

    const apiCall = this.editingEventType
      ? this.httpService.put<APIResponse>(
          `eventhostregions/${this.editingEventType.eventHostRegionId}`,
          payload,
        )
      : this.httpService.post<APIResponse>('eventhostregions', payload);

    apiCall.subscribe({
      next: (response) => {
        if (response.status) {
          this.showSuccessToast(response.message);
          this.dialog.closeAll();
          this.fetchRegions();
          this.editingEventType = null;
        } else {
          this.showErrorToast(response.message);
        }
        this.submitLoading = false;
      },
      error: (err) => {
        console.error('Failed to save Region:', err);
        this.showErrorToast(
          err?.message || 'An error occurred while saving the region.',
        );
        this.submitLoading = false;
      },
    });
  }

  openAddDialog(eventTypeToEdit: EventType | null = null): void {
    this.editingEventType = eventTypeToEdit;
    const initialData = eventTypeToEdit
      ? {
          ...eventTypeToEdit,
          status: this.getStatusBooleanValue(eventTypeToEdit.status),
        }
      : null;

    const dialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: eventTypeToEdit ? 'Edit region' : 'Add new region',
        confirmText: eventTypeToEdit ? 'Update' : 'Submit',
        cancelText: 'Cancel',
        template: this.addEventTypeContent,
        initialData: initialData,
        loading: false,
      },
      width: '50.5rem',
      maxWidth: '100vw',
      disableClose: true,
      autoFocus: false,
    });

    // Create an interval to check submitLoading and update dialog
    const interval = setInterval(() => {
      if (dialogRef.componentInstance) {
        const loadingState = this.submitLoading;
        dialogRef.componentInstance.data.loading = loadingState;
        dialogRef.componentInstance.data.confirmText = this.editingEventType
          ? loadingState
            ? 'Updating...'
            : 'Update'
          : loadingState
            ? 'Submitting...'
            : 'Submit';

        dialogRef.componentInstance.cdr.detectChanges();
      }
    }, 100);

    dialogRef.afterClosed().subscribe(() => {
      clearInterval(interval);
      this.editingEventType = null;
      this.submitLoading = false;
    });
  }

  onEdit(eventType: EventType): void {
    this.openAddDialog(eventType);
  }

  onDelete(user: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure you want to delete ${user.regionName}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.httpService
          .delete<APIResponse>(`eventhostregions/${user.id}`)
          .subscribe({
            next: (response) => {
              if (response?.status) {
                this.fullData = this.fullData.filter((u) => u.id !== user.id);
                this.data = this.data.filter((u) => u.id !== user.id);
                this.adjustPaginationAfterDelete();
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: (err) => {
              this.showErrorToast(err?.message);
            },
          });
      }
    });
  }

  onStatusClick(event: { row: any; id: any }): void {
    if (!this.userRoles.listItem.statusChange) return;
    const { row, id } = event;
    const currentStatus = row.status;
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

    const dialogData: AlertDialogData = {
      title: 'Status update',
      message: `Are you sure you want to ${newStatus === 'Active' ? 'Activate' : 'Deactivate'}?`,
      okText: 'Update',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const apiStatus = newStatus === 'Active';

        this.httpService
          .put<APIResponse>(`eventhostregions/${id}/status`, {
            status: apiStatus,
          })
          .subscribe({
            next: (response) => {
              if (response?.status) {
                this.updateLocalRegionStatus(id, newStatus);
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: () => {
              this.showErrorToast(
                'An error occurred while updating the region status.',
              );
            },
          });
      }
    });
  }

  private updateLocalRegionStatus(regionId: string, newStatus: string): void {
    this.fullData = this.fullData.map((item) => {
      if (item.id === regionId) {
        return {
          ...item,
          status: newStatus,
        };
      }
      return item;
    });

    this.data = this.data.map((item) => {
      if (item.id === regionId) {
        return {
          ...item,
          status: newStatus,
        };
      }
      return item;
    });

    this.cdr.detectChanges();
  }

  goBack() {
    this.location.back();
  }

  private adjustPaginationAfterDelete(): void {
    if (!this.tableComponent) return;

    const totalPages = Math.ceil(
      this.data.length / this.tableComponent.pageSize,
    );
    if (this.tableComponent.currentPage > totalPages && totalPages > 0) {
      this.tableComponent.currentPage = totalPages;
    }
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
